<!-- apps/frontend/src/modules/live-radar/OnboardingTour.vue -->
<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import type { OnboardingStep } from './useOnboarding';
import { computeSpotlightBox, type SpotlightRect } from './onboardingSpotlight';

/**
 * First-run guided tour overlay. Fully controlled by the parent (LiveRadarPage owns the
 * useOnboarding() state) — this component only renders and emits navigation intents, matching
 * the module's page-owns-state / children-emit convention.
 *
 * The overlay intercepts all clicks while visible: the spotlight box is a purely visual ring
 * (drawn via a 9999px box-shadow "punch hole" on a div positioned over the real target), not an
 * actual cutout — the real button underneath stays unreachable until the tour is dismissed.
 */

const props = defineProps<{
  visible: boolean;
  step: OnboardingStep;
  stepIndex: number;
  totalSteps: number;
}>();

const emit = defineEmits<{
  next: [];
  back: [];
  skip: [];
}>();

const spotlight = ref<SpotlightRect | null>(null);

function updateSpotlight(): void {
  if (!props.step.target) {
    spotlight.value = null;
    return;
  }
  const el = document.querySelector(`[data-onboarding-target="${props.step.target}"]`);
  if (!el) {
    spotlight.value = null;
    return;
  }
  spotlight.value = computeSpotlightBox(el.getBoundingClientRect());
}

watch(
  () => [props.visible, props.stepIndex] as const,
  async () => {
    if (!props.visible) {
      spotlight.value = null;
      return;
    }
    await nextTick();
    updateSpotlight();
  },
  { immediate: true },
);

function onResize(): void {
  if (props.visible) updateSpotlight();
}

onMounted(() => window.addEventListener('resize', onResize));
onUnmounted(() => window.removeEventListener('resize', onResize));

const spotlightStyle = computed(() => {
  if (!spotlight.value) return {};
  const s = spotlight.value;
  return {
    top: `${s.top}px`,
    left: `${s.left}px`,
    width: `${s.width}px`,
    height: `${s.height}px`,
  };
});

const isLastStep = computed(() => props.stepIndex === props.totalSteps - 1);
</script>

<template>
  <div
    v-if="visible"
    class="onboarding-overlay"
    :class="{ dim: !spotlight }"
  >
    <div
      v-if="spotlight"
      class="onboarding-spotlight"
      :style="spotlightStyle"
    />

    <div class="onboarding-card">
      <div
        v-if="step.id === 'concept'"
        class="onboarding-anim"
        aria-hidden="true"
      >
        <div class="anim-ripple anim-ripple-1" />
        <div class="anim-ripple anim-ripple-2" />
        <div class="anim-ripple anim-ripple-3" />
        <div class="anim-line anim-line-a" />
        <div class="anim-line anim-line-b" />
        <div class="anim-line anim-line-c" />
        <div class="anim-source" />
        <div class="anim-phone anim-phone-a">
          📱
        </div>
        <div class="anim-phone anim-phone-b">
          📱
        </div>
        <div class="anim-phone anim-phone-c">
          📱
        </div>
        <div class="anim-label">
          Место события
        </div>
      </div>

      <h3 class="onboarding-title">
        {{ step.title }}
      </h3>
      <p class="onboarding-body">
        {{ step.body }}
      </p>

      <div class="onboarding-progress">
        <span
          v-for="i in totalSteps"
          :key="i"
          class="onboarding-dot"
          :class="{ active: i - 1 === stepIndex }"
        />
      </div>

      <div class="onboarding-actions">
        <button
          v-if="stepIndex > 0"
          class="onboarding-back"
          @click="emit('back')"
        >
          Назад
        </button>
        <button
          class="onboarding-skip"
          @click="emit('skip')"
        >
          Пропустить
        </button>
        <button
          class="onboarding-next"
          @click="emit('next')"
        >
          {{ isLastStep ? 'Понятно' : 'Далее' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.onboarding-overlay {
  position: fixed;
  inset: 0;
  z-index: 500;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}
.onboarding-overlay.dim {
  background: rgba(5, 8, 14, 0.72);
}
.onboarding-spotlight {
  position: fixed;
  border-radius: 14px;
  box-shadow: 0 0 0 9999px rgba(5, 8, 14, 0.72);
  border: 2px solid rgba(42, 171, 238, 0.9);
  transition:
    top 0.2s,
    left 0.2s,
    width 0.2s,
    height 0.2s;
}

.onboarding-card {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 420px;
  margin: 0 12px 12px;
  padding: 18px 18px 14px;
  border-radius: 16px 16px 12px 12px;
  background: #161e2c;
  border: 1px solid #243049;
  color: #e6e9ef;
  box-shadow: 0 -4px 24px rgba(0, 0, 0, 0.4);
}
.onboarding-title {
  margin: 0 0 8px;
  font-size: 17px;
  font-weight: 700;
}
.onboarding-body {
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.5;
  color: #c7cee0;
}
.onboarding-progress {
  display: flex;
  gap: 6px;
  margin-bottom: 14px;
}
.onboarding-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #2a3754;
}
.onboarding-dot.active {
  background: #2aabee;
}
.onboarding-actions {
  display: flex;
  align-items: center;
  gap: 10px;
}
.onboarding-back {
  background: none;
  border: none;
  color: #7d879c;
  font-size: 13px;
  cursor: pointer;
  padding: 8px 4px;
}
.onboarding-skip {
  background: none;
  border: none;
  color: #aeb8cc;
  font-size: 13px;
  cursor: pointer;
  padding: 8px 10px;
  margin-right: auto;
}
.onboarding-next {
  background: linear-gradient(135deg, #2aabee 0%, #1f7a44 100%);
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 10px 18px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
}

/* ── Step 1 concept animation: wave from a source reaching 3 phones, then converging back ──
   Pure CSS/SVG-free, one shared 4s loop; timings/positions are illustrative, not to scale. */
.onboarding-anim {
  position: relative;
  width: 220px;
  height: 170px;
  margin: 0 auto 14px;
}
.anim-source {
  position: absolute;
  left: 110px;
  top: 85px;
  width: 10px;
  height: 10px;
  margin: -5px 0 0 -5px;
  border-radius: 50%;
  background: #2aabee;
  z-index: 2;
}
.anim-ripple {
  position: absolute;
  left: 110px;
  top: 85px;
  width: 20px;
  height: 20px;
  margin: -10px 0 0 -10px;
  border-radius: 50%;
  border: 2px solid rgba(42, 171, 238, 0.55);
  animation: onboarding-ripple 4s ease-out infinite;
}
.anim-ripple-2 {
  animation-delay: 0.5s;
}
.anim-ripple-3 {
  animation-delay: 1s;
}
@keyframes onboarding-ripple {
  0% {
    transform: scale(0);
    opacity: 0.7;
  }
  45% {
    opacity: 0.35;
  }
  90%,
  100% {
    transform: scale(8);
    opacity: 0;
  }
}
.anim-phone {
  position: absolute;
  font-size: 20px;
  line-height: 1;
  z-index: 2;
  animation: onboarding-phone-pulse 4s ease-in-out infinite;
}
.anim-phone-a {
  left: 40px;
  top: 30px;
  animation-delay: 0.3s;
}
.anim-phone-b {
  left: 185px;
  top: 45px;
  animation-delay: 0.45s;
}
.anim-phone-c {
  left: 65px;
  top: 140px;
  animation-delay: 0.6s;
}
@keyframes onboarding-phone-pulse {
  0%,
  6%,
  100% {
    transform: scale(1);
  }
  3% {
    transform: scale(1.35);
  }
}
.anim-line {
  position: absolute;
  left: 110px;
  top: 85px;
  height: 2px;
  background: rgba(42, 171, 238, 0.6);
  transform-origin: left center;
  opacity: 0;
}
.anim-line-a {
  width: 89px;
  animation: onboarding-draw-line-a 4s ease-out infinite;
}
.anim-line-b {
  width: 85px;
  animation: onboarding-draw-line-b 4s ease-out infinite;
}
.anim-line-c {
  width: 71px;
  animation: onboarding-draw-line-c 4s ease-out infinite;
}
@keyframes onboarding-draw-line-a {
  0%,
  45% {
    transform: rotate(-142deg) scaleX(0);
    opacity: 0;
  }
  65%,
  88% {
    transform: rotate(-142deg) scaleX(1);
    opacity: 1;
  }
  100% {
    transform: rotate(-142deg) scaleX(1);
    opacity: 0;
  }
}
@keyframes onboarding-draw-line-b {
  0%,
  45% {
    transform: rotate(-28deg) scaleX(0);
    opacity: 0;
  }
  65%,
  88% {
    transform: rotate(-28deg) scaleX(1);
    opacity: 1;
  }
  100% {
    transform: rotate(-28deg) scaleX(1);
    opacity: 0;
  }
}
@keyframes onboarding-draw-line-c {
  0%,
  45% {
    transform: rotate(129deg) scaleX(0);
    opacity: 0;
  }
  65%,
  88% {
    transform: rotate(129deg) scaleX(1);
    opacity: 1;
  }
  100% {
    transform: rotate(129deg) scaleX(1);
    opacity: 0;
  }
}
.anim-label {
  position: absolute;
  left: 50%;
  top: 85px;
  transform: translate(-50%, -50%);
  white-space: nowrap;
  font-size: 12px;
  font-weight: 600;
  color: #cde9fb;
  background: rgba(15, 20, 32, 0.85);
  padding: 3px 8px;
  border-radius: 8px;
  opacity: 0;
  animation: onboarding-label-fade 4s ease-out infinite;
  z-index: 3;
}
@keyframes onboarding-label-fade {
  0%,
  60% {
    opacity: 0;
  }
  75%,
  90% {
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
</style>
